import { useDeleteBlacklistAutoRule } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import type { BlacklistAutoRuleId } from '@hprofits/shared';
import { useConfirm, useToast } from '@hprofits/ui';
import type { Ref } from 'vue';

import { apiClient } from '@/api-client';
import { t } from '@/i18n';

import { readBlacklistErrorMessage } from '../utils/readBlacklistErrorMessage';

export type BlacklistRuleRemoveTarget = Readonly<{ id: BlacklistAutoRuleId; name: string }>;

type UseBlacklistRuleRemoveReturn = Readonly<{
  /** Resolves `true` once the rule is deleted; `false` when the user cancels or the delete fails. */
  remove: (target: BlacklistRuleRemoveTarget) => Promise<boolean>;
  removePending: Readonly<Ref<boolean>>;
}>;

export const useBlacklistRuleRemove = (): UseBlacklistRuleRemoveReturn => {
  const confirm = useConfirm();
  const toast = useToast();
  // `remove` shows its own error toast with the server's detail; the global one would repeat it.
  const mutation = useDeleteBlacklistAutoRule(apiClient, SUPPRESS_ERROR_TOAST_META);

  const remove = async ({ id, name }: BlacklistRuleRemoveTarget): Promise<boolean> => {
    const confirmed = await confirm({
      title: t('modules.blacklistAutoRules.editor.deleteTitle'),
      description: t('modules.blacklistAutoRules.editor.deleteDescription', { name }),
      confirmText: t('modules.blacklistAutoRules.editor.deleteConfirm'),
    });
    if (!confirmed) {
      return false;
    }
    try {
      await mutation.mutateAsync({ ruleId: id });
    } catch (error) {
      toast.error(readBlacklistErrorMessage(error) ?? t('modules.blacklistAutoRules.editor.deleteFailed'));
      return false;
    }
    toast.success(t('modules.blacklistAutoRules.editor.deleted'));
    return true;
  };

  return { remove, removePending: mutation.isPending };
};
